import { AssetGroup, AssetId, AssetRef, AssetInput, AssetOutput, Packet } from "../extension/asset";
import { Asset, ExtendedVirtualCoin, Recipient, VirtualCoin } from "./index";

/**
 * Creates an asset packet from asset inputs and receivers.
 * Groups inputs and outputs by asset ID and creates the Packet object
 * @param assetInputs - map input index -> assets
 * @param receivers - array of recipients with their asset allocations
 * @param changeReceiver - (optional) change receiver containing remaining assets
 * @returns packet containing all asset groups
 */
export function createAssetPacket(
    assetInputs: Map<number, Asset[]>,
    receivers: Recipient[],
    changeReceiver?: Recipient,
): Packet {
    // map inputs by asset id
    const inputsByAssetId = new Map<string, AssetInput[]>();

    for (const [inputIndex, assets] of assetInputs) {
        for (const asset of assets) {
            const existing = inputsByAssetId.get(asset.assetId);
            inputsByAssetId.set(asset.assetId, [
                ...(existing ?? []),
                AssetInput.create(inputIndex, asset.amount),
            ]);
        }
    }

    // map outputs by asset id
    const outputsByAssetId = new Map<string, AssetOutput[]>();

    // track tx output index
    let outputIndex = 0;

    for (const receiver of receivers) {
        if (receiver.assets) {
            for (const asset of receiver.assets) {
                const existing = outputsByAssetId.get(asset.assetId);
                outputsByAssetId.set(asset.assetId, [
                    ...(existing ?? []),
                    AssetOutput.create(outputIndex, asset.amount),
                ]);
            }
        }
        outputIndex++;
    }

    // add change receiver assets if present
    if (changeReceiver?.assets) {
        for (const asset of changeReceiver.assets) {
            const existing = outputsByAssetId.get(asset.assetId);
            outputsByAssetId.set(asset.assetId, [
                ...(existing ?? []),
                AssetOutput.create(outputIndex, asset.amount),
            ]);
        }
    }

    const groups: AssetGroup[] = [];

    // get all unique asset ids from both inputs and outputs
    const allAssetIds = new Set([...inputsByAssetId.keys(), ...outputsByAssetId.keys()]);

    for (const assetIdStr of allAssetIds) {
        const inputs = inputsByAssetId.get(assetIdStr);
        const outputs = outputsByAssetId.get(assetIdStr);

        const assetId = AssetId.fromString(assetIdStr);

        const group = AssetGroup.create(assetId, null, inputs ?? [], outputs ?? [], []);

        groups.push(group);
    }

    return Packet.create(groups);
}

/**
 * Selects coins that contain a specific asset.
 * Returns coins sorted by amount (smallest first for better coin selection).
 */
export function selectCoinsWithAsset(
    coins: ExtendedVirtualCoin[],
    assetId: string,
    requiredAmount: bigint,
): { selected: ExtendedVirtualCoin[]; totalAssetAmount: bigint } {
    // filter only coins that have the specified asset
    const coinsWithAsset = coins.filter((coin) => coin.assets?.some((a) => a.assetId === assetId));

    // sort by asset amount (smallest first for better selection)
    coinsWithAsset.sort((a, b) => {
        const amountA = a.assets?.find((asset) => asset.assetId === assetId)?.amount ?? 0n;
        const amountB = b.assets?.find((asset) => asset.assetId === assetId)?.amount ?? 0n;
        // Array.sort callback returns number; reduce the bigint diff to
        // -1/0/1 (the only thing sort actually consults).
        return amountA < amountB ? -1 : amountA > amountB ? 1 : 0;
    });

    const selected: ExtendedVirtualCoin[] = [];
    let totalAssetAmount = 0n;

    for (const coin of coinsWithAsset) {
        if (totalAssetAmount >= requiredAmount) break;

        selected.push(coin);
        const assetAmount = coin.assets?.find((a) => a.assetId === assetId)?.amount ?? 0n;
        totalAssetAmount += assetAmount;
    }

    if (totalAssetAmount < requiredAmount) {
        throw new Error(
            `Insufficient asset balance: have ${totalAssetAmount}, need ${requiredAmount}`,
        );
    }

    return { selected, totalAssetAmount };
}

export function computeAssetChange(
    inputAssets: Map<string, bigint>,
    outputAssets: Map<string, bigint>,
): Map<string, bigint> {
    const change = new Map<string, bigint>();

    for (const [assetId, inputAmount] of inputAssets) {
        const outputAmount = outputAssets.get(assetId) ?? 0n;
        const changeAmount = inputAmount - outputAmount;
        if (changeAmount > 0n) {
            change.set(assetId, changeAmount);
        }
    }

    return change;
}

export function selectedCoinsToAssetInputs(selectedCoins: VirtualCoin[]): Map<number, Asset[]> {
    const assetInputs = new Map<number, Asset[]>();

    for (let inputIndex = 0; inputIndex < selectedCoins.length; inputIndex++) {
        const coin = selectedCoins[inputIndex];
        if (!coin.assets || coin.assets.length === 0) {
            continue;
        }
        assetInputs.set(inputIndex, coin.assets);
    }

    return assetInputs;
}
